import React, { useState } from "react"
import { twMerge } from "tailwind-merge"
import { AiOutlinePlus, AiOutlineMinus } from "react-icons/ai"
import InfoIcon from "components/icons/InfoIcon"
import Button from "components/Form/Button"
import { motion } from "framer-motion"
import { useLang } from "hooks/useLang"
import { useRouter } from "next/router"
import WaitlistSelect from "./WaitlistSelect";
import TimeRange from "components/atoms/TimeRange";
import SAR from "components/Sar/SAR"
interface BaseProps {
    className?: string
    variant: "day" | "time" | "ticket"
    isFirst?: boolean
    isLast?: boolean
    onClick?: () => void
}

interface DayVariantProps extends BaseProps {
    variant: "day"
    day: string
    date: string
    isSelected?: boolean
    isSoldOut?: boolean
    hasWaitlist?: boolean
}

interface TimeVariantProps extends BaseProps {
    variant: "time";
    startTime: string | Date;
    endTime: string | Date;
    isDisabled?: boolean;
    isSelected?: boolean;
}

interface TicketVariantProps extends BaseProps {
    variant: "ticket"
    title?: string
    price?: number
    quantity?: number
    onIncrease?: (e: React.MouseEvent) => void
    onDecrease?: (e: React.MouseEvent) => void
    increaseDisabled?: boolean
    decreaseDisabled?: boolean
    statusMessage?: string
    statusColor?: string
    discountPreviousValue?: number
    hasOffer?: boolean
    offerText?: string
    isSoldOut?: boolean
    isStartWaitlist?: boolean
    validationMessage?: string
    label?: string
}

type SelectionCardProps =
    | DayVariantProps
    | TimeVariantProps
    | TicketVariantProps

//🟢--------- Day Variant ---------🟢//
const DayVariant = ({
    day,
    date,
    onClick,
    isSelected,
    isFirst,
    isLast,
    isSoldOut = false,
    hasWaitlist = false,
}: DayVariantProps) => {
    const { t } = useLang()
    const { query } = useRouter()
    const borderRadiusClass =
        isFirst && isLast
            ? "rounded-2xl"
            : isFirst
              ? "rounded-t-2xl"
              : isLast
                ? "rounded-b-2xl"
                : ""

    const selectedClass = isSelected
        ? "bg-mint-green-500 text-white hover:bg-mint-green-500"
        : "text-black"

    const soldOutClass = isSoldOut
        ? "cursor-not-allowed hover:bg-gray-700 bg-gray-700"
        : ""

    const isGuvali =
        query.eventid === process.env.NEXT_PUBLIC_CURRENT_GUVALI_ID ||
        query.eventid === "guvali"

    const isLastDay =
        date === "31 December" || date === "31 ديسمبر" || date === "31 Dec"

    return (
        <div
            className={twMerge(
                `relative flex flex-row flex-1 w-full transition-colors duration-200 bg-[#fff]   ${selectedClass} ${soldOutClass}`,
                borderRadiusClass
            )}
        >
            <div className="flex justify-center items-center px-4 w-[40%] lg:w-[30%] ltr:border-r rtl:border-l border-black">
                <h2 className="">{day}</h2>
            </div>
            <div className="flex items-center justify-between w-full">
                <h2 className="px-4 lg:px-10">{date}</h2>

                {isGuvali && isLastDay && (
                    <div className="w-fit bg-[#FE4A36] rounded-xl py-1 px-4 text-white text-xs capitalize-all">
                        {t("new_year")}
                    </div>
                )}

                {isSoldOut ? (
                    <h2 className="text-white text-sm lg:text-lg px-2 lg:px-5">
                        {t("tickets_sold_out")}
                    </h2>
                ) : hasWaitlist ? (
                    <div className="w-fit bg-orange-500 rounded-xl py-1 px-4 text-white text-xs capitalize-all">
                        {t("waitlist_available")}
                    </div>
                ) : (
                    <div
                        onClick={onClick}
                        className={`h-full transition-all duration-200 ${
                            isSelected
                                ? "opacity-0 invisible"
                                : "opacity-100 visible"
                        }`}
                    >
                        <div className="bg-mint-green-500 text-white h-full w-12 lg:w-16 flex justify-center items-center hover:bg-mint-green-600 transition-colors duration-200 cursor-pointer">
                            <AiOutlinePlus className="text-white font-bold" />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

//🟢--------- Time Variant ---------🟢//
const TimeVariant = ({ startTime, endTime, onClick, isDisabled = false, isSelected = false }: TimeVariantProps) => {
    const handleClick = () => {
        if (isDisabled) return
        onClick?.()
    }
    const selectedClass = isSelected
        ? "bg-mint-green-500 text-white hover:bg-mint-green-500"
        : "text-black"

    const disabledClass = isDisabled
        ? "cursor-not-allowed opacity-50"
        : "cursor-pointer"

    // const soldOutClass = isSoldOut
    //     ? "cursor-not-allowed bg-gray-700"
    //     : ""


    return (
       <div
            className={
                twMerge(    
                `flex flex-row flex-1 w-full bg-[#fff] ${
                    disabledClass
                } ${
                    selectedClass
                }`)
            }
            onClick={handleClick}
        >
             <div className="flex items-center px-4 lg:px-10 w-full">
                <TimeRange startTime={startTime} endTime={endTime} />
            </div>
            <div
                className={`${
                    isDisabled
                        ? "bg-gray-400"
                        : "bg-mint-green-500 hover:bg-mint-green-600"
                } px-5 flex items-center justify-center`}
            >
                +
            </div>
        </div>
    )
}

//🟢--------- Ticket Variant ---------🟢//
const TicketVariant = ({
    title = "Ticket",
    price = 0,
    quantity = 0,
    onIncrease,
    onDecrease,
    onClick,
    statusMessage,
    statusColor,
    isStartWaitlist,
    increaseDisabled = false,
    decreaseDisabled = false,
    discountPreviousValue = 0,
    hasOffer = false,
    offerText = "",
    isSoldOut = false,
    validationMessage,
    label,
}: TicketVariantProps) => {
    const { t } = useLang()
 
    const isSelectionDisabled = isSoldOut
    const handleCardClick = () => {
        if (isSelectionDisabled) return
        onClick?.()
    }

    const baseButtonClass =
        "outline-none border-none w-7 h-7 lg:w-9 lg:h-9 text-white rounded-full flex items-center justify-center cursor-pointer"

    const disabledButtonClass =
        "bg-mint-green-500 bg-opacity-20 cursor-not-allowed hover:bg-mint-green-500 hover:bg-opacity-20"

    const enabledButtonClass = "bg-mint-green-500 hover:bg-mint-green-700"

    const soldOutClass = isSoldOut ? "cursor-not-allowed bg-gray-700" : ""
    // const waitListClass = isStartWaitlist ? "bg-gray-700" : ""

    // const showWaitListCard = isStartWaitlist && isSoldOut
    const isTicketsAvailable = !isSoldOut && !isStartWaitlist
    const [showWaitlistButtons, setShowWaitlistButtons] = useState(false)
    return (
        <div
            className={twMerge(
                "relative flex flex-row w-full px-4 py-2 lg:px-10 gap-x-5  items-center bg-[#fff]",
                soldOutClass
            )}
        >
            <div
                className={twMerge(
                    "col flex flex-col flex-[2] justify-center cursor-pointer",
                    soldOutClass
                )}
                onClick={handleCardClick}
            >
                <div className="flex items-center gap-x-1">
                    <div className="flex gap-x-1.5 md:gap-x-2.5 items-center">
                        <h2 className="text-[#201750] capitalize text-sm lg:text-md leading-5">
                            {title}
                        </h2>
                        <InfoIcon className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 p-0.5 rounded-full border border-black border-opacity-70 text-black" />
                    </div>
                </div>

                <div className="flex items-center gap-x-2">
                    <h2 className="text-[#201750] font-extrabold text-sm lg:text-lg">
                        <SAR color={"#201750"} price={price} />
                    </h2>
                    {discountPreviousValue > 0 && (
                        <span className="text-[#201750] lg:text-sm text-[10px] leading-3 lg:leading-normal line-through decoration-red-500 decoration-2 self-end">
                            <SAR
                                price={discountPreviousValue}
                                color="#201750"
                            />
                        </span>
                    )}
                </div>
            </div>
            <div className="flex gap-x-1">
                {hasOffer && !isSoldOut && offerText && (
                    <span
                        className="w-fit bg-[#FE4A36] rounded-xl py-1 px-4 text-white text-xs mb-1 capitalize"
                        style={{
                            background:
                                "linear-gradient(90deg, #A819E7 0%, #DC0FCE 100%)",
                        }}
                    >
                        {offerText}
                    </span>
                )}

                {isStartWaitlist && (
                    <span className="w-fit bg-[#FE4A36] rounded-xl py-1 px-4 text-white text-xs capitalize-all">
                        {t("waitlist")}
                    </span>
                )}
            </div>
            {( label) && (
    <div className="flex items-center">
        {label && (
            <h2 className="text-center lg:text-sm text-[10.5px] font-medium leading-3 px-2 py-1 rounded-xl w-fit text-white"
             style={{     background:
                                "linear-gradient(90deg, #A819E7 0%, #DC0FCE 100%)", }}
            >
                {label}
            </h2>
        )}
        {statusMessage && (
            <h2
                className="text-center lg:text-sm text-[10.5px] italic leading-3"
                style={{ color: statusColor }}
            >
                {statusMessage}
            </h2>
        )}
    </div>
)}
            {statusMessage && (
                <div className="col flex-1">
                    <h2
                        className="text-center lg:text-sm text-[10.5px] italic leading-3"
                        style={{ color: statusColor }}
                    >
                        {statusMessage}
                    </h2>
                </div>
            )}
            {/* {validationMessage && (
                <div className="col flex-1">
                    <h2 className="text-center lg:text-sm text-[10.5px] italic leading-3 text-red-500">
                        {validationMessage}
                    </h2>
                </div>
            )} */}
            <div className="col flex items-center justify-end gap-x-3 md:gap-x-5">
                <div className="flex items-center gap-x-2 lg:gap-x-5">
                    {isStartWaitlist ? (
                        <WaitlistSelect
                            onDecrease={onDecrease}
                            onIncrease={onIncrease}
                            quantity={quantity}
                            // isSelectionDisabled={isSelectionDisabled}
                            increaseDisabled={increaseDisabled}
                            decreaseDisabled={decreaseDisabled}
                            waitlistActions={{
                                showWaitlistButtons,
                                setShowWaitlistButtons,
                            }}
                        />
                    ) : null}
                    {isSoldOut ? (
                        <h2 className="text-white text-sm lg:text-lg">
                            {t("tickets_sold_out")}
                        </h2>
                    ) : null}
                    {isTicketsAvailable ? (
                        <div className="flex items-center gap-x-2 lg:gap-x-5">
                            <Button
                                onClick={onDecrease}
                                theme="custom"
                                className={twMerge(
                                    baseButtonClass,
                                    decreaseDisabled
                                        ? disabledButtonClass
                                        : enabledButtonClass
                                )}
                            >
                                <AiOutlineMinus className="text-white font-bold" />
                            </Button>
                            <span className="text-black font-bold w-4 text-center">
                                {quantity}
                            </span>
                            <Button
                                onClick={onIncrease}
                                theme="custom"
                                className={twMerge(
                                    baseButtonClass,
                                    increaseDisabled
                                        ? disabledButtonClass
                                        : enabledButtonClass
                                )}
                            >
                                <AiOutlinePlus className="text-white font-bold" />
                            </Button>
                        </div>
                    ) : null}
                </div>
            </div>
        </div>
    )
}

//🔵🔵---------- Selection Card ----------🔵🔵//
const SelectionCard = (props: SelectionCardProps) => {
    const { className = "", isFirst, isLast, onClick } = props

    // Add rounded corners to the first and last card
    const roundedClass =
        isFirst && isLast
            ? "rounded-2xl"
            : isFirst
              ? "rounded-t-2xl"
              : isLast
                ? "rounded-b-2xl"
                : ""

    const renderContent = () => {
        switch (props.variant) {
            case "day":
                return (
                    <DayVariant
                        variant={props.variant}
                        className={className}
                        day={props.day}
                        date={props.date}
                        onClick={onClick}
                        isSelected={props.isSelected}
                        isFirst={isFirst}
                        isLast={isLast}
                        isSoldOut={props.isSoldOut}
                        hasWaitlist={props.hasWaitlist}
                    />
                )
            case "time":
                return (
                    <TimeVariant
                        variant={props.variant}
                        className={className}
                                                startTime={props.startTime}
                        endTime={props.endTime}
                        isDisabled={(props as TimeVariantProps).isDisabled}
                        isSelected={(props as TimeVariantProps).isSelected}
                        onClick={onClick}
                    />
                )
            case "ticket":
                return (
                    <TicketVariant
                        variant={props.variant}
                        className={className}
                        title={props.title}
                        price={props.price}
                        quantity={props.quantity}
                        onIncrease={props.onIncrease}
                        onDecrease={props.onDecrease}
                        increaseDisabled={props.increaseDisabled}
                        decreaseDisabled={props.decreaseDisabled}
                        statusMessage={props.statusMessage}
                        statusColor={props.statusColor}
                        discountPreviousValue={props.discountPreviousValue}
                        onClick={onClick}
                        hasOffer={props.hasOffer}
                        offerText={props.offerText}
                        isSoldOut={props.isSoldOut}
                        isStartWaitlist={props.isStartWaitlist}
                        validationMessage={props.validationMessage}
                        label={props.label}
                    />
                )
            default:
                return null
        }
    }

    return (
        <motion.div
            initial={{ opacity: 0, x: 40 }} // start
            animate={{ opacity: 1, x: 0 }} // finish
            transition={{ duration: 0.3 }}
            className={twMerge(
                `text-sm lg:text-md w-full min-h-[56px] sm:min-h-[74px] flex overflow-hidden`,
                roundedClass,
                className
            )}
        >
            {renderContent()}
        </motion.div>
    )
}

export default SelectionCard
